// src/_utils/index.ts

// ─── Number / Currency ───────────────────────────────────────
export function formatCurrency(n: number): string {
  return n.toLocaleString('th-TH')
}

export function parseCurrency(s: string): number {
  return parseFloat(s.replace(/,/g, '')) || 0
}

// ─── Date ────────────────────────────────────────────────────
export function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('th-TH', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

export function formatDateShort(ts: number): string {
  return new Date(ts).toLocaleDateString('th-TH', {
    year: 'numeric', month: '2-digit', day: '2-digit',
  })
}

// ─── Loan Calculator ─────────────────────────────────────────
export interface LoanCalcResult {
  actualAmount: number
  interestAmount: number
  totalWithInterest: number
  dailyPayment: number
  firstDeduction: number   // หักงวดแรกทันที
  payoutAmount: number     // เงินที่โอนจริงให้ลูกค้า
}

export function calcLoan(
  amount: number,
  interestRate: number,     // %
  installmentDays: number
): LoanCalcResult {
  // ── สูตรคำนวณสินเชื่อพื้นฐาน (แก้สูตรตรงนี้ได้เลย) ──
  const interestAmount    = Math.round((amount * interestRate) / 100)        // ดอกเบี้ย = ยอดกู้ × ดอก% ÷ 100
  const totalWithInterest = amount + interestAmount                          // ยอดรวมทั้งสัญญา = ยอดกู้ + ดอกเบี้ย
  const dailyPayment      = Math.round(totalWithInterest / installmentDays)  // ผ่อนต่อวัน = ยอดรวม ÷ จำนวนวัน
  const firstDeduction    = dailyPayment                                     // หักงวดแรก = ผ่อนต่อวัน (หักทันทีทุกครั้ง)
  // เงินโอนจริงให้ลูกค้า = ยอดกู้ − งวดแรก  (ใช้กับสัญญาใหม่ที่ไม่มีหนี้ค้างเดิม)
  // ตัวอย่าง: กู้ 5,000,000 / 30 วัน ดอก 20% → ต่อวัน 200,000 → โอนจริง 4,800,000
  const payoutAmount      = amount - dailyPayment

  return {
    actualAmount: amount,
    interestAmount,
    totalWithInterest,
    dailyPayment,
    firstDeduction,
    payoutAmount,
  }
}

// ─── Customer ID generator ───────────────────────────────────
export function genCustomerId(uid: string): string {
  const ts   = Date.now()
  const rand = Math.random().toString(36).slice(2, 8)
  return `${uid}_${ts}_${rand}`
}

// ─── Zone display ────────────────────────────────────────────
export function displayZone(zone: string | string[]): string {
  if (Array.isArray(zone)) return zone.join('+')
  return zone || '-'
}

// ─── Bootstrap alert helpers (ใช้แทน dangerAlert / successAlert) ──
export function toastDanger(msg: string) {
  window.dispatchEvent(new CustomEvent('ktfc-toast', { detail: { msg, type: 'danger' } }))
}

export function toastSuccess(msg: string) {
  window.dispatchEvent(new CustomEvent('ktfc-toast', { detail: { msg, type: 'success' } }))
}

export function toastWarning(msg: string) {
  window.dispatchEvent(new CustomEvent('ktfc-toast', { detail: { msg, type: 'warning' } }))
}

// ─── Safe number ─────────────────────────────────────────────
export const safeNum = (v: any): number => {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

export const safeStr = (v: any): string => (v == null ? '' : String(v))